import { useCallback } from 'react'
import {
  CreateUserGroupInput,
  UserGroupFragment,
  UserGroupFragmentDoc,
  UpdateUserGroupInput,
  useCreateUserGroupMutation,
  useDeleteUserGroupMutation,
  useUpdateUserGroupMutation,
  UserFragment,
  UserFragmentDoc,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'

type Hook = {
  createUserGroup: (input: CreateUserGroupInput) => Promise<void>
  updateUserGroup: (id: string, input: UpdateUserGroupInput) => Promise<void>
  deleteUserGroup: (id: string) => Promise<void>
}

export function useUserGroups(): Hook {
  const [createUserGroupMutation] = useCreateUserGroupMutation()
  const [updateUserGroupMutation] = useUpdateUserGroupMutation()
  const [deleteUserGroupMutation] = useDeleteUserGroupMutation()
  const cacheHelpers = useCacheHelpers()

  const createUserGroup = useCallback(
    async (input: CreateUserGroupInput) => {
      await createUserGroupMutation({
        variables: { input },
        optimisticResponse: {
          createUserGroup: {
            __typename: 'UserGroup',
            id: input.id,
            name: input.name,
            users: input.userIds.map((id) =>
              cacheHelpers.getFragment<UserFragment>(id, UserFragmentDoc),
            ),
          },
        },
        update: (_, mutationResult) => {
          const userGroup = mutationResult.data?.createUserGroup
          if (!userGroup) {
            return
          }

          cacheHelpers.addItemToListInRootQuery(
            UserGroupFragmentDoc,
            userGroup,
            'userGroups',
          )
        },
        refetchQueries: ['Me'],
      })
    },
    [cacheHelpers, createUserGroupMutation],
  )

  const updateUserGroup = useCallback(
    async (id: string, input: UpdateUserGroupInput) => {
      const userGroup = cacheHelpers.getFragment<UserGroupFragment>(
        id,
        UserGroupFragmentDoc,
      )

      await updateUserGroupMutation({
        variables: { id, input },
        optimisticResponse: {
          updateUserGroup: {
            ...userGroup,
            name: input.name ?? userGroup.name,
            users: input.userIds
              ? input.userIds.map((userId) =>
                  cacheHelpers.getFragment<UserFragment>(
                    userId,
                    UserFragmentDoc,
                  ),
                )
              : userGroup.users,
          },
        },
        refetchQueries: ['Me'],
      })
    },
    [cacheHelpers, updateUserGroupMutation],
  )

  const deleteUserGroup = useCallback(
    async (id: string) => {
      await deleteUserGroupMutation({
        variables: { id },
        optimisticResponse: {
          deleteUserGroup: true,
        },
        update: () => {
          cacheHelpers.removeItemFromListInRootQuery(
            UserGroupFragmentDoc,
            id,
            'userGroups',
          )
        },
        refetchQueries: ['Me'],
      })
    },
    [cacheHelpers, deleteUserGroupMutation],
  )

  return {
    createUserGroup,
    updateUserGroup,
    deleteUserGroup,
  }
}
